import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listCreditNotes } from '@/app/services/creditNoteService';
import { cancelVoucher } from '@/app/services/voucherService';
import { Button } from '@/components/ui/button';
import { Loader2, Plus } from 'lucide-react';
import { CreditNoteDataTable } from './CreditNoteDataTable';
import { createColumns, CreditNoteTableItem } from './columns';
import { toast } from 'sonner';
import { ConfirmationDialog } from '@/app/components/ConfirmationDialog';
import { PaginatedResponse } from '@/app/types/api';
import { VoucherResponse } from '@/app/types/voucher';

const CreditNotesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for pagination and search
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // State for cancellation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [creditNoteToCancel, setCreditNoteToCancel] = useState<CreditNoteTableItem | null>(null);
  
  // Hardcode the Credit Note voucher type ID - this is ID 5 from the provided data
  const creditNoteVoucherTypeId = 5;

  // Fetch credit notes
  const { 
    data: creditNotesData = { items: [], total: 0 }, 
    isLoading: isLoadingCreditNotes
  } = useQuery<PaginatedResponse<VoucherResponse>>({
    queryKey: ['creditNotes', currentPage, pageSize, searchQuery, creditNoteVoucherTypeId],
    queryFn: () => listCreditNotes({
      page: currentPage + 1, // API uses 1-based indexing
      limit: pageSize,
      search_query: searchQuery,
      filters: JSON.stringify({ voucher_type_id: creditNoteVoucherTypeId })
    }),
  });

  // Handle pagination changes
  const handlePaginationChange = (pageIndex: number, newPageSize: number) => {
    setCurrentPage(pageIndex);
    if (pageSize !== newPageSize) {
      setPageSize(newPageSize);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(0); // Reset to first page on new search
  };

  // Calculate page count based on total records
  const pageCount = Math.ceil((creditNotesData?.total || 0) / pageSize);

  // Cancel Credit Note Mutation
  const cancelCreditNoteMutation = useMutation({
    mutationFn: (id: number) => cancelVoucher(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] });
      queryClient.invalidateQueries({ queryKey: ['creditNote', variables] });
      toast.success('Credit note cancelled successfully. Accounting entries have been reversed.');
      setCancelDialogOpen(false);
      setCreditNoteToCancel(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel credit note: ${error.message}`);
      setCancelDialogOpen(false);
      setCreditNoteToCancel(null);
    },
  });

  // Handler for viewing credit note details
  const handleView = (creditNote: CreditNoteTableItem) => {
    navigate(`/credit-notes/detail/${creditNote.id}`);
  };

  // Handler for cancelling credit note
  const handleCancel = (creditNote: CreditNoteTableItem) => {
    setCreditNoteToCancel(creditNote);
    setCancelDialogOpen(true);
  };

  // Confirm cancellation
  const confirmCancel = () => {
    if (creditNoteToCancel) {
      cancelCreditNoteMutation.mutate(creditNoteToCancel.id);
    }
  };

  // Create columns with action handlers
  const columns = useMemo(
    () => createColumns({ onView: handleView, onCancel: handleCancel }),
    []
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold tracking-tight">Credit Notes</h1>
        <Button onClick={() => navigate('/credit-notes/new')} disabled={isLoadingCreditNotes}>
          <Plus className="mr-2 h-4 w-4" /> New Credit Note
        </Button>
      </div>

      {(isLoadingCreditNotes || cancelCreditNoteMutation.isPending) && creditNotesData.items.length === 0 ? (
        <div className="flex justify-center my-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <CreditNoteDataTable
          columns={columns}
          data={creditNotesData.items}
          isLoading={isLoadingCreditNotes || cancelCreditNoteMutation.isPending}
          onSearch={handleSearch}
          pagination={{
            pageIndex: currentPage,
            pageSize: pageSize,
            pageCount: pageCount,
            onPaginationChange: handlePaginationChange,
          }}
        />
      )}

      {/* Cancellation Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setCreditNoteToCancel(null);
        }}
        onConfirm={confirmCancel}
        title={`Cancel Credit Note ${creditNoteToCancel?.voucher_number}?`}
        description={`This action will cancel the credit note and reverse all associated accounting entries.\n\nThis action cannot be undone.`}
        confirmText="Yes, Cancel Credit Note"
        cancelText="No, Keep It"
        isLoading={cancelCreditNoteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
};

export default CreditNotesPage; 